import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEdit, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { MessageTemplate } from '@clientwire/api';
import { MessageTemplateDialogComponent } from './message-template-dialog/message-template-dialog.component';
import { MessageTemplatesService } from './message-templates.service';
import { ButtonComponent } from '@akostm/orbit-ui';

@Component({
  selector: 'lib-settings-message-templates',
  standalone: true,
  imports: [CommonModule, TranslateModule, FontAwesomeModule, DialogModule, ButtonComponent],
  templateUrl: './message-templates.component.html',
  styleUrls: ['./message-templates.component.scss'],
})
export class MessageTemplatesComponent implements OnInit {
  readonly #messageTemplateService = inject(MessageTemplatesService);
  readonly #dialog = inject(Dialog);
  readonly #translateService = inject(TranslateService);

  // Message templates data
  readonly messageTemplates = this.#messageTemplateService.messageTemplates;
  readonly loading = this.#messageTemplateService.loading;
  readonly error = this.#messageTemplateService.error;

  // Font Awesome icons
  readonly editIcon = faEdit;
  readonly deleteIcon = faTrash;
  readonly addIcon = faPlus;

  ngOnInit(): void {
    this.#messageTemplateService.loadMessageTemplates();
  }

  addMessageTemplate(): void {
    const dialogRef = this.#dialog.open(MessageTemplateDialogComponent, {
      data: {
        title: this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.ADD_TEMPLATE'),
        confirmLabel: this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.CREATE'),
      },
      width: '44rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#messageTemplateService.createMessageTemplate(result);
      }
    });
  }

  editMessageTemplate(template: MessageTemplate): void {
    const dialogRef = this.#dialog.open(MessageTemplateDialogComponent, {
      data: {
        title: this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.EDIT_TEMPLATE'),
        confirmLabel: this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.SAVE'),
        template: template,
      },
      width: '44rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#messageTemplateService.updateMessageTemplate(template.name, result);
      }
    });
  }

  deleteMessageTemplate(template: MessageTemplate): void {
    const dialogRef = this.#dialog.open(MessageTemplateDialogComponent, {
      data: {
        title: this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.CONFIRM_DELETE_TITLE'),
        confirmLabel: this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.DELETE'),
        message: this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.CONFIRM_DELETE', {
          name: template.name,
        }),
        isConfirmation: true,
      },
      width: '32rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#messageTemplateService.deleteMessageTemplate(template.name);
      }
    });
  }

  formatParameters(parameters: any[]): string {
    if (!parameters || parameters.length === 0) {
      return this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.NO_PARAMETERS');
    }

    return parameters.map((param) => param.name).join(', ');
  }

  formatTexts(texts: Record<string, string>): string {
    if (!texts || Object.keys(texts).length === 0) {
      return this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.NO_TEXTS');
    }

    return Object.keys(texts).join(', ');
  }
}
